/**
 * Dialog to create a team and optionally attach initial members via searchable user lists.
 *
 * Where: `Teams` page or anywhere “new team” is triggered.
 * Uses: `teamApi` mutations, infinite user search.
 * Behavior: form state + validation; closes on success and invalidates team queries.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import type { TeamUser, TeamUserResponse } from '@/types/team';
import type { InfiniteData } from '@tanstack/react-query';

interface TeamCreateModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  user_roles?: string;
}

interface UserRoleRow {
  user_uuid: string;
  user_display: string; // For showing in UI
  role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
}

export const TeamCreateModal: React.FC<TeamCreateModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [userRoles, setUserRoles] = useState<UserRoleRow[]>([]);

  // User search state
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserForRole, setSelectedUserForRole] = useState<TeamUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<'SERVICE_MANAGER' | 'SERVICE_MEMBER'>('SERVICE_MEMBER');
  const trimmedUserSearch = userSearch.trim();
  const shouldSearchUsers = trimmedUserSearch.length > 0;

  // User search query - using searchAllUsers like InviteUserModal
  const {
    data: usersPages,
    isLoading: isLoadingUsers,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetchingNextPage: isFetchingNextUsersPage,
  } = useInfiniteQuery<
    TeamUserResponse,
    Error,
    InfiniteData<TeamUserResponse, string | undefined>,
    [string, string],
    string | undefined
  >({
    queryKey: ['project-create-users-search', trimmedUserSearch],
    queryFn: ({ pageParam }) =>
      teamApi.searchAllUsers({
        page_token: pageParam,
        max_results: 10,
        search: trimmedUserSearch,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
    enabled: open && shouldSearchUsers,
  });

  const searchedUsers: TeamUser[] =
    usersPages?.pages.flatMap((page: TeamUserResponse) => page.output.items) ?? [];

  const usersLoadMoreRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll observer
  useEffect(() => {
    if (
      !shouldSearchUsers ||
      !usersLoadMoreRef.current ||
      !hasNextUsersPage ||
      !fetchNextUsersPage
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextUsersPage && !isFetchingNextUsersPage) {
          fetchNextUsersPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(usersLoadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextUsersPage, isFetchingNextUsersPage, fetchNextUsersPage, shouldSearchUsers]);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!formData.name.trim()) e.name = 'Team name is required';
    return e;
  };

  const createMutation = useMutation({
    mutationFn: () =>
      teamApi.createTeam({
        name: formData.name.trim(),
        notes: formData.notes || undefined,
        user_roles:
          userRoles.length > 0
            ? userRoles.map((ur) => ({ user_uuid: ur.user_uuid, role: ur.role }))
            : undefined,
      }),
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      onClose();
      const teamKey = resp?.output?.key || resp?.output?.uuid;
      if (teamKey) {
        window.location.href = `/teams/${teamKey}`;
      }
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error_messages?.[0];
      if (msg && msg.toLowerCase().includes('already exists')) {
        setErrors((prev) => ({ ...prev, name: 'A team with this name already exists' }));
      }
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({ name: '', notes: '' });
      setErrors({});
      setUserRoles([]);
      setUserSearch('');
      setSelectedUserForRole(null);
      setSelectedRole('SERVICE_MEMBER');
    }
  }, [open]);

  const handleChange = (field: keyof FormData) => (e: any) => {
    const value = e?.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field in errors) setErrors((prev) => ({ ...prev, [field as keyof FormErrors]: undefined }));
  };

  const handleAddUserRole = () => {
    if (!selectedUserForRole) return;

    const userUuid = (selectedUserForRole.uuid || selectedUserForRole.key) as string;
    const userDisplay = selectedUserForRole.name || selectedUserForRole.email || userUuid;

    // Check if this user+role combo already exists
    const exists = userRoles.some((ur) => ur.user_uuid === userUuid && ur.role === selectedRole);
    if (exists) {
      setErrors((prev) => ({ ...prev, user_roles: 'This user already has this role' }));
      return;
    }

    setUserRoles((prev) => [...prev, { user_uuid: userUuid, user_display: userDisplay, role: selectedRole }]);
    setSelectedUserForRole(null);
    setUserSearch('');
    setErrors((prev) => ({ ...prev, user_roles: undefined }));
  };

  const handleRemoveUserRole = (index: number) => {
    setUserRoles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create Team</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Team Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Unique name for this team'}
            disabled={createMutation.isPending}
            required
          />

          <TextField
            label="Notes"
            multiline
            minRows={3}
            value={formData.notes}
            onChange={handleChange('notes')}
            disabled={createMutation.isPending}
            helperText="Additional notes about this team"
          />

          <Divider sx={{ my: 1 }} />

          {/* User Role Assignment Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              User Roles (Optional)
            </Typography>

            {/* User Search */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <TextField
                placeholder="Search users by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                fullWidth
                disabled={createMutation.isPending}
                size="small"
                helperText={!shouldSearchUsers ? 'Type to search for users' : undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {shouldSearchUsers && (
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {isLoadingUsers && searchedUsers.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <List dense>
                    {searchedUsers.map((user) => {
                      const label = user.name || user.email || 'User';
                      const secondary = user.email && user.name ? user.email : '';
                      const isSelected = selectedUserForRole?.key === user.key;
                      return (
                        <ListItemButton
                          key={user.key}
                          selected={isSelected}
                          onClick={() => setSelectedUserForRole(user)}
                        >
                          <ListItemText primary={label} secondary={secondary} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                  <div ref={usersLoadMoreRef} style={{ height: 16 }} />
                  {isFetchingNextUsersPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}
                  {searchedUsers.length === 0 && !isLoadingUsers && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Role selection and add button */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as 'SERVICE_MANAGER' | 'SERVICE_MEMBER')}
                    label="Role"
                    disabled={createMutation.isPending}
                  >
                    <MenuItem value="SERVICE_MANAGER">Service Manager</MenuItem>
                    <MenuItem value="SERVICE_MEMBER">Service Member</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddUserRole}
                  disabled={!selectedUserForRole || createMutation.isPending}
                  variant="outlined"
                  size="small"
                >
                  Add User Role
                </Button>
                {selectedUserForRole && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {selectedUserForRole.name || selectedUserForRole.email}
                  </Typography>
                )}
              </Box>
            </Box>

            {errors.user_roles && (
              <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                {errors.user_roles}
              </Typography>
            )}

            {/* Added User Roles List */}
            {userRoles.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {userRoles.map((ur, index) => (
                  <Box
                    key={`${ur.user_uuid}-${ur.role}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {ur.user_display}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                      {ur.role === 'SERVICE_MANAGER' ? 'Service Manager' : 'Service Member'}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveUserRole(index)}
                      disabled={createMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {createMutation.isError && <Alert severity="error">Failed to create team.</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={createMutation.isPending ? <CircularProgress size={20} /> : undefined}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
